import { measureLoadPhase } from 'src/lib/analytics/gameLoadTracking';
import {
  getApplication,
  getFooter,
  getRootView,
  waitForItPromise,
  withLoading,
} from 'src/lib/utils';
import { openPopupPromise } from 'src/lib/popups/popupOpenClose';
import StateObserver, { user, now } from '../../../StateObserver';
import * as Inventory from '../../../replicant/inventory';
import {
  trackPinchEvent,
  trackSequenceFinish,
  trackSequenceStart,
} from '../../../lib/analytics/events';
import { startEnergyPinchSequence } from '../../shop/Helpers';
import sounds from '../../../lib/sounds';
import { inviteFriendUnlockFlow } from 'src/game/social/social.sequence';
import {
  match2BoosterCatalog,
  Match2BoosterType,
  match2BuruStreakRewards,
  match2PowerupCatalog,
  Match2PowerupType,
  PuzzleResults,
} from 'src/replicant/match2/match2.ruleset';
import { getTutorialController } from '../../tutorial/TutorialController';
import { petIdleSequence } from 'src/game/pet/pet.sequence';
import { getGameScene } from './game/getGameScene';
import {
  getBuruStreakPowerups,
  getMatch2Level,
  isMatch2TimedRewardActive,
} from 'src/replicant/match2/match2.getters';
import { setCompensation } from 'src/game/inventory/inventory.redux';
import { getGemsToEnergyExchangeRates } from 'src/replicant/inventory/inventory.getters';
import { match2TournamentPostGame } from './tournament/match2-tournament.sequence';
import { openRaceSequence } from './races/races.sequence';
import {
  getActiveRace,
  getRaceStartTime,
} from 'src/replicant/match2/races/races.getters';
import {
  Match2PuzzleStartData,
  PuzzleStartData,
  match2SendPuzzleFinishAnalytics,
  match2SendPuzzleStartAnalytics,
  trackFrenzyLevelComplete,
} from './match2.analytics';
import { saveMatch2GameplayState } from './savestate/match2-savestate.utils';
import {
  match2DailyChallengePostGame,
  matchDailyChallengeProgress,
} from './dailychallenge/match2-dailychallenge.sequence';
import { match2FrenzyPostGame } from './frenzy/match2-frenzy.sequence';
import { getPuzzleMap, match2LoadNextRegionAssets } from './match2.assets';
import { BlockId } from './game/defs/block';
import { BlockGenAlgorithm } from './game/defs/map';
import { leaguePostGameSequence } from './leagues/league.sequences';
import {
  FixedBPointsReward,
  FixedGemsReward,
  Match2Reward,
} from 'src/replicant/match2/rewards/match2-rewards.ruleset';
import { magicLampsPostGame } from './magiclamps/magiclamps.sequence';
import { setHudVisibility } from 'src/state/ui';
import { hasValidSaveState } from 'src/replicant/match2/savestate/match2-savestate.getters';
import {
  getCurrentTeamCompetition,
  getTeamMembers,
  teamPostGameSequence,
} from './team/team.sequence';
import {
  getCurrentTeamEventId,
  getCurrentTeamEventType,
} from 'src/replicant/team/team.getters';
import { streakBattleRoyalePostGame } from 'src/game/streakbattleroyale/streakbattleroyale.sequence';
import { Feature } from 'src/replicant/analytics';
import features from 'src/replicant/ruleset/features';
import i18n from 'src/lib/i18n/i18n';
import { loadMatch2LevelAssets } from 'src/loadMatch2LevelAssets';
import { Stats } from 'src/game/stats';
import { startInstallAppRewardSequence } from 'src/game/native/native.sequence';
import { preloadRewardedVideoAd } from 'src/game/ads/ads.util';
import { trackSessionStateSave } from 'src/game/crossplay/crossplay.tracking';
import NakedPromise from './lib/pattern/NakedPromise';
import { animatePuzzleRewards } from 'src/game/animation/rewardAnimation';
import type { Match2ProgressDailyChallengeArgs } from 'src/replicant/match2/dailychallenge/match2-dailychallenge.actions';
import { puzzleCoinsIsPlayingTutorial } from 'src/replicant/puzzlecoins/puzzlecoins.getters';
import { inGamePopupAfterLevelWin } from 'src/game/launch/ingamePopup.sequence';
import { superLollipopPostGame } from 'src/game/superLollipop/superLollipop.sequence';
import {
  isSuperLollipopActive,
  isSuperLollipopAvailable,
} from 'src/replicant/superLollipop/superLollipop.getters';
import { isWeeklyLeaderboardsAvailable } from 'src/replicant/weeklyLeaderboard/weeklyLeaderboard.getters';
import {
  getWeeklyLeaderboardRanking,
  updateUserInWeeklyLeaderboardFriendsCache,
  updateUserInWeeklyLeaderboardGlobalCache,
} from 'src/game/weeklyLeaderboard/weeklyLeaderboard.utils';
import { weeklyLeaderboardPostGame } from 'src/game/weeklyLeaderboard/weeklyLeaderboard.sequence';
import { treasureExpressPostGame } from 'src/game/treasureExpress/treasureExpress.sequence';
import { canSeeArena } from 'src/replicant/championsLeague/championsLeague.getters';
import { championsLeaguePostGame } from 'src/game/championsLeague/championsLeague.sequence';
import { captureGenericError } from 'src/lib/sentry';
import { captureMessage } from '@sentry/browser';
import { View } from '@play-co/timestep-core/ui';
import * as match2TournamentGetters from 'src/replicant/match2/tournament/match2-tournament.getters';
import { promptLINELoginNew } from 'src/game/guest/guest.sequence';
import PNsManager from 'src/game/native/push.notifications';
import { isGuestUser } from 'src/replicant/guest/guest.getters';
import { buddiesPostPuzzleSequence } from 'src/game/buddies/buddies.sequence';
import {
  cardsAutoOpenFirstTime,
  cardsRevealReceivedRewards,
} from 'src/game/cards/cards.sequence';
import { postPlayPromoGroups } from 'src/game/promo/promo.launch';
import { dreamDriftIsPlaying } from 'src/game/dreamDrift/dreamDrift.utils';

/**
 * Store games played in current session in a row, without quiting
 */
export let sessionNonSkipGamesPlayed = 0;

/** Number of completed puzzles this session, including quit results. */
let sessionPuzzleLevelsPlayed = 0;

/** Whether a recurring promo has already displayed from post-play this session. */
let hasShownRecurringPostPlayPromo = false;

/**
 * Play match2 puzzle
 */
export async function startMatch2(args?: {
  selectedPowerups?: Match2PowerupType[];
  onSceneEntered?: () => void;
  onPuzzleFinish?: () => void;
  level?: number;
  coinsPinch?: boolean;
}) {
  // Direct starts and retries must not replace a Dream Drift scene.
  if (dreamDriftIsPlaying()) {
    // Record blocked entry so unexpected no-op Play actions can be investigated.
    captureMessage('Match2 start blocked by Dream Drift', 'warning');
    resolveMatch2SequencePromise();
    return;
  }

  const selectedPowerups = args?.selectedPowerups ?? [];

  // Current match2 level
  const puzzleLevel = args?.level ?? getMatch2Level(user(), true);

  // Check if we're loading froma saved state.
  const loadingFromSave = hasValidSaveState(user(), now());

  if (!loadingFromSave) {
    // Check if user has enough energy to play match2, if not show energy shop
    const hasEnergy = await match2EnergyCheck();

    // Skip if user has no energy
    if (!hasEnergy) return;
  }

  // Load match2 assets
  // The first puzzle of a new player loads under the splash. Later puzzles are
  // not measured, the tracker ignores everything after the splash is gone.
  const mapDef = await measureLoadPhase('firstScene', () =>
    match2LoadGame(puzzleLevel),
  );

  // Skip if somehow assets failed to load
  if (!mapDef) return;

  // Create a match2 promise to await the game completion
  createMatch2SequencePromise();

  // STATS - Increment puzzle attempts counters
  if (!loadingFromSave) await Stats.api.startPuzzleEvent(user());

  //  ANALYTICS - Send puzzle start analytics data and store it for sending with puzzle finish analytics later
  const puzzleStartData = match2SendPuzzleStartAnalytics(
    selectedPowerups,
    mapDef,
    puzzleLevel,
  );

  preloadRewardedVideoAd('puzzle-start');

  if (!loadingFromSave) {
    // Start match2 on backend
    await StateObserver.invoke.startMatch2Puzzle({ level: puzzleLevel });
  } else {
    // Resume match2 on backend, marking the match2.playing flag as true
    await StateObserver.invoke.resumeMatch2PuzzleFromSaveState();
  }

  // Start loading next region in the background.
  void match2LoadNextRegionAssets();

  const superLollipopActive = isSuperLollipopActive(user());

  // The number of attempts to finish a level is actually the same as the losing streak
  const puzzleAttempts = user().match2.losingStreak;

  // Open the match2 game screen and initialize the game scene
  const game = await match2OpenGame({
    selectedPowerups,
    puzzleStartData,
  });

  // Run callback if provided.
  args?.onSceneEntered?.();

  // === GAMEPLAY =============================================================

  // Await the gameplay session to complete
  await game.waitForComplete();

  // Start buruburu music after leaving gameplay.
  sounds.music.play('bgm');

  // Recovery-aborted games must close without submitting a puzzle result.
  if (game.aborted) {
    await match2CloseGame();
    resolveMatch2SequencePromise();
    return;
  }

  // ==========================================================================

  const puzzleResults = game.getPuzzleResults();
  const dynamicBlockGenType = game.dynamicBlockGenType;

  // Broken puzzle, restart logic
  if (puzzleResults.result === 'broken') {
    await openPopupPromise('popupModalWhite', {
      title: i18n('minigame.match2.brokenPuzzlePopup.title'),
      message: i18n('minigame.match2.brokenPuzzlePopup.message'),
      button: i18n('minigame.match2.brokenPuzzlePopup.button'),
      tracking: {
        feature: Feature.MATCH2._,
        $subFeature: Feature.MATCH2.BROKEN_PUZZLE,
      },
    });

    // Restart level if puzzle is brocken (no more moves)
    void waitForItPromise(1).then(async () => {
      // Give back energy and powerups
      await StateObserver.invoke.match2RestartLevel(selectedPowerups);

      // Close the game screen
      await match2CloseGame();

      // Start again with same powerups
      void startMatch2({ selectedPowerups, level: puzzleLevel });
    });

    return;
  }

  // Count every completed puzzle result, including a player quitting the level.
  sessionPuzzleLevelsPlayed++;

  // Preserve the non-quit counter used by the native-app play prompt.
  if (puzzleResults.result !== 'quit') {
    sessionNonSkipGamesPlayed++;
  }

  // Lock menus during match2 post-game sequence
  blockHud();

  try {
    // Run post-game sequence, and check if user will play again (e.g. from post-game popups)
    const willPlayAgain = await postGameSequence({
      puzzleLevel,
      puzzleResults,
      puzzleStartData,
      selectedPowerups,
      puzzleAttempts,
      superLollipopActive,
      dynamicBlockGenType,
      onPuzzleFinish: args?.onPuzzleFinish,
      coinsPinch: args?.coinsPinch,
    });

    // Resolve the match2 promise after the post-game sequence is complete, if not playing again
    if (!willPlayAgain) {
      resolveMatch2SequencePromise();
    }
  } catch (error) {
    // Report the error
    captureGenericError('Match2SequenceError', error);
    // Resolve the match2 promise in case of error
    resolveMatch2SequencePromise();
  } finally {
    // Unlock menus after match2 post-game sequence
    unblockHud();
  }
}

/**
 * View to block HUD interactions
 */
let hudBlocker: View | undefined;

/**
 * Block HUD interactions, to prevent user from clicking on the header/footer and home buttons
 */
function blockHud() {
  if (hudBlocker) return;
  hudBlocker = new View({
    superview: getRootView(),
    infinite: true,
    zIndex: 9997, // Just one index before the popups
    canHandleEvents: true,
  });
}

/**
 * Unblock HUD interactions
 */
function unblockHud() {
  if (!hudBlocker) return;
  hudBlocker.removeFromSuperview();
  hudBlocker = undefined;
}

/**
 * Post-game sequence that will run after the match2 puzzle is completed.
 * @param args - Arguments for the post-game sequence.
 * @returns True if user has selected to play again, false otherwise.
 */
async function postGameSequence(args: {
  puzzleLevel: number;
  puzzleResults: PuzzleResults;
  puzzleStartData: PuzzleStartData;
  selectedPowerups: Match2PowerupType[];
  puzzleAttempts: number;
  superLollipopActive?: boolean;
  dynamicBlockGenType?: BlockGenAlgorithm;
  onPuzzleFinish?: () => void;
  coinsPinch?: boolean;
}): Promise<boolean> {
  // RACES - Update race score.
  await StateObserver.invoke.addMatch2RaceScore({ goal: 'level', score: 1 });

  // Check if user has just beaten a level
  const hasWon = args.puzzleResults.result === 'win';

  // Complete the match2 gameplay
  const rewards = await completeMatch2Puzzle({
    level: args.puzzleLevel,
    result: hasWon ? 'win' : 'lose',
    blocksBroken: args.puzzleResults.blocksBroken,
    stars: args.puzzleResults.score?.stars ?? 3,
    hasContinued: args.puzzleResults.usedContinues > 0,
  });

  // DAILY CHALLENGES - Update daily challenges if the feature is available for the player
  const dailyChallengeProgress = await matchDailyChallengeProgress(
    args.puzzleResults,
    args.puzzleStartData,
  );

  // ANALYTICS - Send puzzle finish analytics event
  match2SendPuzzleFinishAnalytics(
    args.selectedPowerups,
    args.puzzleStartData,
    args.puzzleResults,
    args.puzzleLevel,
    args.puzzleAttempts,
    undefined, // rescueLevelPuzzleFinishProperties
    args.dynamicBlockGenType,
    rewards.some((reward) => reward.type === 'card'),
  );

  // Stats - Increment puzzle attempts counters
  if (args.puzzleResults.result !== 'broken') {
    await Stats.api.completePuzzleEvent(user(), args.puzzleResults.result);
  }

  // Run puzzle finish callback if provided.
  args?.onPuzzleFinish?.();

  // If the player has won, set compensation rewards and open the level complete popup
  if (hasWon) {
    // Present the regular level complete popup
    await match2LevelWin(args.puzzleResults, rewards, args.puzzleLevel);
  }

  // Close the game screen
  await match2CloseGame();

  // Trigger post-game popups.
  trackSequenceStart('PostLevel');
  const willPlayAgain = await postGamePopups(
    args.puzzleLevel,
    args.puzzleResults,
    rewards,
    dailyChallengeProgress,
    args?.coinsPinch,
    args.superLollipopActive,
    args.puzzleAttempts,
  );
  trackSequenceFinish('PostLevel');

  // SC In-game Popup A/B (0477): extra OA Add-Friend ask at high-intent level
  // wins for still-unsubscribed users. Wins only, never during the tutorial
  // (the tutorial's own post-game ask is handled post-toy-care), and not when
  // the user is bouncing straight into another puzzle.
  if (hasWon && !willPlayAgain && !puzzleCoinsIsPlayingTutorial(user())) {
    await inGamePopupAfterLevelWin(args.puzzleLevel);
  }

  // Run the champions league post game sequence
  if (hasWon && canSeeArena(user(), now())) {
    await championsLeaguePostGame();
  }

  // Hud can be unblocked from this point - tutorials like teams might require HUD interaction
  unblockHud();

  // Trigger any post-game tutorials.
  await getTutorialController().handleEvent(
    hasWon ? 'match2LevelCompleted' : 'match2LevelFailed',
  );

  // Invalidate team competition cache.
  void getCurrentTeamCompetition()?.invalidate();

  // Also invalidate team members cache.
  void getTeamMembers()?.invalidate();

  // Invalidate weekly leaderboard event ranking cache.
  void getWeeklyLeaderboardRanking()?.invalidate();

  // Update user in weekly leaderboard global cache
  updateUserInWeeklyLeaderboardGlobalCache();

  // Pre-fetch the new ranking so the home button can be updated.
  void getWeeklyLeaderboardRanking()?.get();

  // Update user in weekly leaderboard friends cache
  updateUserInWeeklyLeaderboardFriendsCache();

  // Treatment users should receive one recurring promo after returning home
  // from at least two completed puzzles in the current session.
  const shouldLaunchRecurringPostPlayPromo =
    !willPlayAgain &&
    !hasShownRecurringPostPlayPromo &&
    sessionPuzzleLevelsPlayed >= 2 &&
    features(user()).suppressFeatureIntros;

  // Keep retrying on later home returns until an eligible item is displayed.
  if (shouldLaunchRecurringPostPlayPromo) {
    hasShownRecurringPostPlayPromo = await postPlayPromoGroups();
  }

  // Show pet idle sequence
  await petIdleSequence();

  // Clear any remaining compensation (safety net for cases where reward animation was skipped)
  StateObserver.dispatch(setCompensation({ bPoints: 0, gems: 0 }));

  // Return if user has chosen to play again
  return willPlayAgain;
}

/**
 * Run post game popups, and return true if user will play again from one of these popups
 * @param puzzleLevel
 * @param puzzleResults
 * @param rewards
 * @returns
 */
async function postGamePopups(
  puzzleLevel: number,
  puzzleResults: PuzzleResults,
  rewards: Match2Reward[],
  dailyChallengeProgress?: Match2ProgressDailyChallengeArgs[],
  coinsPinch?: boolean,
  wasSuperLollipopActive?: boolean,
  puzzleAttempts?: number,
): Promise<boolean> {
  const hasWon = puzzleResults.result === 'win';

  // Skip all post-game sequences if playing tutorial
  if (puzzleCoinsIsPlayingTutorial(user())) {
    // Only display rewards in this case
    await match2DisplayRewards(puzzleResults, rewards, dailyChallengeProgress);
    return false;
  }

  // FRENZY POST-GAME CLAIM
  const frenzyRewards = await match2FrenzyPostGame(puzzleResults);

  // STREAK EVENT
  await streakBattleRoyalePostGame();

  // Display gameplay rewards, if any
  await match2DisplayRewards(
    puzzleResults,
    rewards,
    dailyChallengeProgress,
    coinsPinch,
    puzzleAttempts,
    frenzyRewards,
  );

  // PUSH NOTIFICATION - Request PN permissions (always use new flow)
  if (puzzleLevel === 15 && PNsManager.isApiAvailable()) {
    const shouldRemind = await PNsManager.shouldRemindPermissionRequest();
    if (shouldRemind) {
      await PNsManager.requestPermission('postLevel');
    }
  }

  // Suggest LINE login popup if user playing as guest
  if (hasWon && [5, 20, 50].includes(puzzleLevel) && isGuestUser(user())) {
    await promptLINELoginNew('postLevel');
  }

  // RACES - Run races post game sequence
  await openRaceSequence({ auto: true, originFeature: 'postLevel' });

  // BUDDIES - Run buddies post game sequence
  await buddiesPostPuzzleSequence(hasWon);

  // SUPER LOLLIPOP - Run super lollipop post game sequence
  await superLollipopPostGame({
    hasWon,
    wasSuperLollipopActive,
  });

  // WEEKLY LEADERBOARDS - Run weekly leaderboards post game sequence
  await weeklyLeaderboardPostGame(hasWon);

  // TREASURE EXPRESS - Run treasure express popups post game sequence
  const playFromTreasureExpress = await treasureExpressPostGame(hasWon);

  // Exit if user has won and selected "play" in treasure express popup
  if (playFromTreasureExpress) return true;

  // TOURNAMENT - Run tournament post game sequence
  const playFromTournament = await match2TournamentPostGame(hasWon);

  // Exit if user has won and selected "play" in tournament popup
  if (playFromTournament) return true;

  // DAILY CHALLENGES - Show the daily challenge auto-popup if it is complete and not yet claimed
  await match2DailyChallengePostGame();

  // Native app rewards.
  await startInstallAppRewardSequence('postLevel');

  // If the player has lost, show the retry popup that will prompt user to play again or quit
  if (!hasWon) {
    const playingAgain = await match2LevelFailed(puzzleLevel);

    // Restart match2 if retry was selected
    if (playingAgain) return true;
  }

  // Show friend invite unlock for control.
  await inviteFriendUnlockFlow();

  // Show league info popup if needed.
  await leaguePostGameSequence();

  // MAGIC LAMPS - Run magic lamps post game sequence
  await magicLampsPostGame();

  // TEAMS - Run team post game sequence, to claim pending event rewards, if any
  await teamPostGameSequence();

  if (hasWon) {
    await cardsAutoOpenFirstTime('postLevel');
  }

  // Return false if user has not selected to play again
  return false;
}

/**
 * Check if user has enough energy to play match2, if not show energy shop
 */
async function match2EnergyCheck() {
  const { user } = StateObserver.getState();
  const now = StateObserver.now();

  // Return true if unlimited energy is active
  if (isMatch2TimedRewardActive(user, 'energy', now)) {
    return true;
  }

  // Return true if user has energy to play the puzzle
  if (Inventory.getters.getEnergy(user, now) >= 1) {
    return true;
  }

  // Start pinch energy - send pinch event
  trackPinchEvent({ resource: 'energy', trigger: 'match2' });

  // Not enough energy, show energy shop and cancel sequence
  await startEnergyPinchSequence('energy_minigame');

  return false;
}

/**
 * Load match2 game assets
 */
async function match2LoadGame(puzzleLevel: number) {
  const loadingPromise = loadMatch2LevelAssets(puzzleLevel);

  // Show tutorial while waiting for match2 to init.
  await getTutorialController().handleEvent('match2Started');

  // Wait for match2 assets to load
  await withLoading(() => loadingPromise, {
    feature: Feature.MATCH2._,
    subFeature: Feature.MATCH2.LEVEL_ASSETS,
  });

  const mapDef = await getPuzzleMap(puzzleLevel);

  return mapDef;
}

/**
 * Open match2 game screen and initialize the game scene
 */
export async function match2OpenGame(args: {
  selectedPowerups: Match2PowerupType[];
  puzzleStartData: Match2PuzzleStartData;
  playtest?: boolean;
}) {
  const { selectedPowerups, puzzleStartData, playtest } = args;
  // Play music in buruburu app to prevent audio conflict
  sounds.music.play('match2');

  // Ensure that hud (header & footer) is hidden before opening the match2 game screen
  StateObserver.dispatch(setHudVisibility({ header: false, footer: false }));

  const buruStreakPowerups = getBuruStreakPowerups(
    StateObserver.getState().user,
  );

  // Open the minigame scene for match2
  const { screenManager } = getApplication();
  await screenManager.showScreen('minigame', {
    minigame: playtest ? 'match2-playtest' : 'match2',
    levelNumber: puzzleStartData.level,
    selectedPowerups,
    bonusPowerups: buruStreakPowerups,
  });

  // Get the game scene instance
  const game = getGameScene();

  if (!game) {
    throw new Error('Game scene not found after mini-game screen initialized');
  }

  // Set starting boosters on game side for debug analytics purposes
  game.startingBoostersAnalytics = puzzleStartData.startingBoosters;

  return game;
}

/**
 * Close match2 game screen
 */
async function match2CloseGame() {
  const { screenManager } = getApplication();

  // Hide the minigame scene and go back to the pet scene
  await screenManager.showScreen('pet');

  // FRENZY - Update frenzy bar to where it was left off prior to the game
  screenManager.header.refreshMatch2FrenzyBar();
}

/**
 * Present the match2 level complete popup
 */
async function match2LevelWin(
  results: PuzzleResults,
  rewards: Match2Reward[],
  puzzleLevel: number,
) {
  const pointsReward = rewards.find(
    (r: Match2Reward) => r.type === 'bPoints',
  ) as FixedBPointsReward;
  const gemsReward = rewards.find(
    (r: Match2Reward) => r.type === 'gems',
  ) as FixedGemsReward;
  StateObserver.dispatch(
    setCompensation({
      bPoints: pointsReward ? pointsReward.amount : 0,
      gems: gemsReward ? gemsReward.amount : 0,
    }),
  );

  await openPopupPromise('popupMatch2LevelComplete', {
    useClickOutside: true,
    level: puzzleLevel,
    stars: results.score?.stars,
    origin: 'postLevel',
  });
}

/**
 * Present the match2 level failed popup, and return true if retry was selected
 */
async function match2LevelFailed(puzzleLevel: number) {
  // If the player has lost, show the retry popup that will prompt user to play again or quit
  const result = await openPopupPromise('popupMatch2LevelFailed', {
    useClickOutside: true,
    level: puzzleLevel,
    origin: 'postLevel',
  });

  // Result can potentially be undefined if `closeAllPopups` is called.
  if (!result) return false;

  // Restart match2 if retry was selected
  if (result && result.retry) {
    startMatch2({ selectedPowerups: result.powerups });
    return true;
  }

  return false;
}

/**
 * Display match2 rewards, if any
 */
async function match2DisplayRewards(
  results: PuzzleResults,
  rewards: Match2Reward[],
  dailyChallengeProgress?: Match2ProgressDailyChallengeArgs[],
  coinsPinch?: boolean,
  puzzleAttempts?: number,
  frenzyRewards?: Match2Reward[],
) {
  const hasWon = results.result === 'win';
  // Find rewards that can be displayed in the grant rewards popup
  const displayableRewards = rewards.filter((reward) => {
    // Do not display score on target races.
    if (
      reward.type === 'feature' &&
      reward.subType === 'race_points' &&
      getActiveRace(getRaceStartTime(reward.raceId))?.config.type === 'target'
    ) {
      return false;
    }
    return true;
  });

  const superLollipopAvailable = isSuperLollipopAvailable(user());
  const weeklyLeaderboardsAvailable = isWeeklyLeaderboardsAvailable(
    user(),
    StateObserver.now(),
  );
  const hasRewardsToDisplay =
    displayableRewards.length > 0 || superLollipopAvailable;

  // If the player has won and there are displayable rewards, play the puzzle reward animations
  if (hasWon && hasRewardsToDisplay) {
    const stars = results.score?.stars;
    await animatePuzzleRewards({
      rewards: displayableRewards,
      stars,
      dailyChallengeProgress,
      shorterAnimation: coinsPinch,
      superLollipopProgress: superLollipopAvailable,
      weeklyLeaderboardProgress: weeklyLeaderboardsAvailable,
      puzzleAttempts,
      frenzyRewards,
      compensate: true,
      onBeforeRewards: () => getFooter().minigameButton.playLevelUp(),
    });
    await cardsRevealReceivedRewards(displayableRewards);
  }
}

/**
 * Check if user has enough energy to play match2, if not show energy shop
 */
export async function runEnergyCheckSequence(exchangeIndex = 0) {
  const user = StateObserver.getState().user;
  const now = StateObserver.now();

  // Always allow playing if frenzy reward for energy is active, regardless of energy amount
  if (isMatch2TimedRewardActive(user, 'energy', now)) {
    return true;
  }

  let playable = true;
  const hasNoEnergy = () => Inventory.getters.getEnergy(user, now) === 0;

  if (hasNoEnergy()) {
    const { items, bonus, premium } = getGemsToEnergyExchangeRates(
      StateObserver.getState().user,
    )[exchangeIndex];
    const success = await openPopupPromise('popupMatch2Pinch', {
      type: 'energy',
      amount: items + bonus,
      price: premium,
      origin: 'pinch',
    });

    playable = success;

    if (success) {
      await openPopupPromise('popupEnergyExchanged', {
        items,
        bonus,
        price: premium,
        origin: 'pinch',
      });
    }
  }

  return playable;
}

/**
 * Purchase booster sequence
 */
export async function promptPurchaseBooster(
  type: Exclude<Match2BoosterType, 'continue'>,
  save: boolean = true,
) {
  if (save) {
    // Save current gameplay state, in case of reload/redirect after purchase.
    await saveMatch2GameplayState('boosterPurchase');

    trackSessionStateSave({
      feature: 'puzzle_booster',
      subFeature: `puzzle_booster_${type}`,
      timestamp: now(),
      origin: 'purchase', // To match the `SessionStateLoad` event.
    });
  }

  // Show booster purchase popup.
  const result = await openPopupPromise('popupConfirmMatch2Booster', {
    type,
    ...match2BoosterCatalog[type],
    origin: 'pinch',
  });

  if (result) {
    await StateObserver.invoke.purchaseMatch2Booster({ type });
  }

  // Return result.
  return result;
}

/**
 * Purchase powerup sequence
 */
export async function promptPurchasePowerup(type: Match2PowerupType) {
  // Show powerup purchase popup.
  const result = await openPopupPromise('popupConfirmMatch2Powerup', {
    type,
    ...match2PowerupCatalog[type],
    origin: 'pinch',
  });

  if (result) {
    await StateObserver.invoke.purchaseMatch2Powerup({ type });
  }

  // Return result.
  return result;
}

/**
 * Complete match2 puzzle and return rewards and send analytics events.
 */
export async function completeMatch2Puzzle(opts: {
  level: number;
  result: 'win' | 'lose';
  blocksBroken: Partial<Record<BlockId, number>>;
  stars: number;
  hasContinued: boolean;
}): Promise<Match2Reward[]> {
  const { rewards, events } = await StateObserver.invoke.completeMatch2Puzzle({
    ...opts,
  });

  for (const event of events) {
    trackFrenzyLevelComplete(event);
  }

  // Add team competition points if applicable.
  const eventId = getCurrentTeamEventId(now());
  const currentCompetition = getCurrentTeamCompetition()?.getCached();
  const joined = currentCompetition?.joined ?? false;
  const eventType = getCurrentTeamEventType(now());

  // By default, user can always score team points
  let canScore = true;

  // But if the current event is a chest event, user might not be able to score if event is already claimed
  if (eventId && eventType === 'chest') {
    // Find out current event status
    const eventStatus = currentCompetition?.status;
    // Can only score if event is not claimed yet
    canScore = eventStatus !== 'complete-already-claimed';
  }

  // Only add team points reward if there is an event, user is in a team, and event is not claimed yet
  if (eventId && joined && eventType && canScore) {
    rewards.push({
      type: 'feature',
      subType: `team_${eventType}_event`,
      amount: opts.stars,
    });
  }

  return rewards;
}

/**
 * Promise that will resolve only after the match2 sequence is finished including playing again,
 * in case the user has selected to play again from one of the post-game popups.
 */
let match2SequencePromise: NakedPromise<void> | null = null;

/**
 * Create the match2 sequence promise, if not already created
 */
function createMatch2SequencePromise() {
  if (match2SequencePromise) return;
  match2SequencePromise = new NakedPromise();
}

/**
 * Resolve the match2 sequence promise, if it exists and set to null
 */
export function resolveMatch2SequencePromise() {
  match2SequencePromise?.resolve();
  match2SequencePromise = null;
}

/**
 * Await for the whole match2 sequence, including playing again.
 */
export async function waitForMatch2Sequence() {
  if (!match2SequencePromise) return;
  await match2SequencePromise;
}

/**
 * Check if the match2 sequence is currently playing
 */
export function isPlayingMatch2Sequence() {
  return !!match2SequencePromise;
}

/**
 * Run 1st move pinch retry sequence (2nd pinch after the first one closes)
 */
export function firstMovePinchRetrySequence(price: number) {
  const maxBuruStreak = match2BuruStreakRewards.length;
  const buruStreak = Math.min(user().match2.buruStreak.value, maxBuruStreak);

  return openPopupPromise('popupMatch2MovePinchRetry', {
    price,
    objectivesCount: getGameScene()?.getFrenzyObjectivesCount() ?? 0,
    buruStreak,
    origin: 'pinch',
  });
}

/**
 * Run 2nd move pinch retry sequence (3rd pinch after the second one closes)
 */
export function secondMovePinchRetrySequence(price: number) {
  // While super lollipop is available, show only its pinch retry popup, which will also
  // display the tournament progress loss, if any.
  if (isSuperLollipopAvailable(user())) {
    if (isSuperLollipopActive(user())) {
      return openPopupPromise('popupSuperLollipopPinchRetry', {
        price,
        origin: 'pinch',
      });
    }
  }
  // Otherwise, show the pinch retry popup for the tournament multiplier.
  else {
    const multiplierIndex =
      match2TournamentGetters.getTournamenCurrentMultiplierIndex(user(), now());
    // only for when player got a tournament multiplier ongoing
    if (multiplierIndex > 0) {
      return openPopupPromise('popupMatch2PinchTournamentStreak', {
        price,
        origin: 'pinch',
      });
    }
  }

  return false;
}
